import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ROUTES, ROLES } from '../config/app.config';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { PublicRoute } from '../components/PublicRoute';
import { LoadingFallback } from '../components/LoadingFallback';

// Direct imports for public and 404
import { HomePage } from '../pages/Home/HomePage';
import { LoginPage } from '../pages/Login/LoginPage';
import { ForgotPasswordPage } from '../pages/ForgotPassword/ForgotPasswordPage';
import { ResetPasswordPage } from '../pages/ResetPassword/ResetPasswordPage';
import { NotFoundPage } from '../pages/NotFound/NotFoundPage';

// Lazy-loaded protected pages
const DashboardPage = lazy(() => import('../pages/Dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const OrdersPage = lazy(() => import('../pages/Orders/OrdersPage').then((m) => ({ default: m.OrdersPage })));
const LocalOrdersPage = lazy(() => import('../pages/LocalOrders/LocalOrdersPage').then((m) => ({ default: m.LocalOrdersPage })));
const PurchaseOrdersPage = lazy(() => import('../pages/PurchaseOrders/PurchaseOrdersPage').then((m) => ({ default: m.PurchaseOrdersPage })));
const ClientsPage = lazy(() => import('../pages/Clients/ClientsPage').then((m) => ({ default: m.ClientsPage })));
const ProductsPage = lazy(() => import('../pages/Products/ProductsPage').then((m) => ({ default: m.ProductsPage })));
const UsersPage = lazy(() => import('../pages/Users/UsersPage').then((m) => ({ default: m.UsersPage })));
const SectionsPage = lazy(() => import('../pages/Sections/SectionsPage').then((m) => ({ default: m.SectionsPage })));
const ArticlePage = lazy(() => import('../pages/Article/ArticlePage').then((m) => ({ default: m.ArticlePage })));
const StockDashboardPage = lazy(() =>
  import('../pages/StockDashboard/StockDashboardPage').then((m) => ({ default: m.StockDashboardPage }))
);

// In this app: admin and seller (where seller = shop_manager)
const managerRoles = [ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.MANAGER]; // MANAGER = shop_manager = seller
const adminRoles = [ROLES.ADMIN, ROLES.SUPER_ADMIN];

export function AppRoutes() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path={ROUTES.HOME} element={<HomePage />} />
        <Route
          path={ROUTES.LOGIN}
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path={ROUTES.FORGOT_PASSWORD}
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path={ROUTES.RESET_PASSWORD}
          element={
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          }
        />

        <Route
          path={ROUTES.DASHBOARD}
          element={
            <ProtectedRoute allowedRoles={managerRoles}>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ORDERS}
          element={
            <ProtectedRoute allowedRoles={managerRoles}>
              <OrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.LOCAL_ORDERS}
          element={
            <ProtectedRoute allowedRoles={managerRoles}>
              <LocalOrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.PURCHASE_ORDERS}
          element={
            <ProtectedRoute allowedRoles={adminRoles}>
              <PurchaseOrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CLIENTS}
          element={
            <ProtectedRoute allowedRoles={managerRoles}>
              <ClientsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.PRODUCTS}
          element={
            <ProtectedRoute allowedRoles={managerRoles}>
              <ProductsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.USERS}
          element={
            <ProtectedRoute allowedRoles={adminRoles}>
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SECTIONS}
          element={
            <ProtectedRoute allowedRoles={adminRoles}>
              <SectionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ARTICLE}
          element={
            <ProtectedRoute allowedRoles={managerRoles}>
              <ArticlePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STOCK}
          element={
            <ProtectedRoute allowedRoles={managerRoles}>
              <StockDashboardPage />
            </ProtectedRoute>
          }
        />

        <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to={ROUTES.NOT_FOUND} replace />} />
      </Routes>
    </Suspense>
  );
}
